import React, { useEffect, useId, useRef } from "react";
import type { CreditReportParseResultDto, ParsedTradelineDto } from "../../api/aiApi";

export interface CreditReportParseModalProps {
  open: boolean;
  onClose: () => void;
  parseResult: CreditReportParseResultDto | null;
  onChangeTradelines: (next: ParsedTradelineDto[]) => void;
  onApply: () => void;
  applying: boolean;
  error: string;
}

export const CreditReportParseModal: React.FC<CreditReportParseModalProps> = ({
  open,
  onClose,
  parseResult,
  onChangeTradelines,
  onApply,
  applying,
  error,
}) => {
  const titleId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    cancelRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !parseResult) {
    return null;
  }

  const negative = parseResult.tradelines?.filter((t) => t.isNegative) ?? [];

  const toggleTradeline = (target: ParsedTradelineDto, checked: boolean) => {
    const list = parseResult.tradelines ?? [];
    onChangeTradelines(
      list.map((t) => (t === target ? { ...t, selected: checked } : t))
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-slate-700 bg-slate-800 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} className="text-lg font-semibold text-white">
          IntelliWarrior AI — credit report analysis
        </h2>

        {error ? <p className="mt-3 text-sm text-rose-300">{error}</p> : null}

        <div className="mt-6 space-y-6">
          <div>
            <h3 className="mb-2 font-semibold text-white">Credit scores found</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-lg bg-slate-700 p-3 text-center">
                <p className="text-xs text-slate-400">Equifax</p>
                <p className="text-2xl font-bold text-white">
                  {parseResult.scores?.equifax ?? "—"}
                </p>
              </div>
              <div className="rounded-lg bg-slate-700 p-3 text-center">
                <p className="text-xs text-slate-400">Experian</p>
                <p className="text-2xl font-bold text-white">
                  {parseResult.scores?.experian ?? "—"}
                </p>
              </div>
              <div className="rounded-lg bg-slate-700 p-3 text-center">
                <p className="text-xs text-slate-400">TransUnion</p>
                <p className="text-2xl font-bold text-white">
                  {parseResult.scores?.transUnion ?? "—"}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-slate-700/50 p-4">
            <h3 className="mb-2 font-semibold text-white">Summary</h3>
            <div className="grid grid-cols-2 gap-3 text-center sm:grid-cols-5">
              <div>
                <p className="text-2xl font-bold text-white">{parseResult.summary?.totalAccounts ?? 0}</p>
                <p className="text-xs text-slate-400">Total accounts</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-red-400">{parseResult.summary?.negativeItems ?? 0}</p>
                <p className="text-xs text-slate-400">Negative items</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-400">{parseResult.summary?.collections ?? 0}</p>
                <p className="text-xs text-slate-400">Collections</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-400">{parseResult.summary?.latePayments ?? 0}</p>
                <p className="text-xs text-slate-400">Late payments</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-400">{parseResult.summary?.inquiries ?? 0}</p>
                <p className="text-xs text-slate-400">Inquiries</p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-2 font-semibold text-white">Negative items found ({negative.length})</h3>
            <p className="mb-3 text-sm text-slate-400">Select items to create dispute entries:</p>
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {negative.length === 0 ? (
                <p className="text-sm text-slate-500">No negative tradelines detected.</p>
              ) : (
                negative.map((item) => (
                  <label
                    key={`${item.creditorName}-${item.accountNumber}-${item.balance}`}
                    className="flex cursor-pointer items-start gap-3 rounded-lg bg-slate-700/50 p-3 hover:bg-slate-700"
                  >
                    <input
                      type="checkbox"
                      checked={item.selected !== false}
                      onChange={(e) => toggleTradeline(item, e.target.checked)}
                      className="mt-1 rounded border-slate-500"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-white">{item.creditorName}</p>
                      <p className="text-sm text-slate-400">
                        {item.accountType} · ${Number(item.balance).toLocaleString()} · {item.status}
                      </p>
                      <p className="text-xs text-slate-500">{(item.bureaus ?? []).join(", ")}</p>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>

          {(parseResult.inquiries?.length ?? 0) > 0 && (
            <div>
              <h3 className="mb-2 font-semibold text-white">Inquiries ({parseResult.inquiries!.length})</h3>
              <div className="space-y-1">
                {parseResult.inquiries!.map((inq) => (
                  <div
                    key={`${inq.creditorName}-${inq.date}-${inq.bureau}`}
                    className="flex justify-between rounded bg-slate-700/30 px-3 py-2 text-sm"
                  >
                    <span className="text-white">{inq.creditorName}</span>
                    <span className="text-slate-400">
                      {inq.bureau} · {inq.date || "Unknown date"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(parseResult.summary?.recommendedActions?.length ?? 0) > 0 && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4">
              <h3 className="mb-2 font-semibold text-emerald-400">Recommended actions</h3>
              <ul className="space-y-1">
                {parseResult.summary!.recommendedActions!.map((action, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                    <span className="mt-0.5 text-emerald-400">→</span>
                    {action}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-3 border-t border-slate-700 pt-4">
            <button
              type="button"
              onClick={onApply}
              disabled={applying}
              className="flex-1 rounded-lg bg-emerald-600 py-3 font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {applying ? "Applying…" : "Apply to client profile"}
            </button>
            <button
              ref={cancelRef}
              type="button"
              onClick={onClose}
              className="rounded-lg bg-slate-700 px-6 py-3 text-slate-300 transition-colors hover:bg-slate-600"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
